import { Quote, Star, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading, Accent } from "@/components/Reveal";
import { GOOGLE_REVIEW } from "@/lib/content";
import { useLang } from "@/lib/i18n";

const GoogleRating = () => {
  const { t, lang } = useLang();
  const rating = GOOGLE_REVIEW.rating.toLocaleString(lang === "en" ? "en-GB" : "bg-BG", { minimumFractionDigits: 1 });
  return (
    <div data-testid="google-rating" className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 border border-line bg-ink/80 px-8 py-6 mb-12">
      <div className="flex items-center gap-5">
        <svg width="34" height="34" viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
          <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.8 2.2 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.3 17.7 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z" />
          <path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
          <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.3 0-11.6-3.8-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
        </svg>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim">{t.testimonials.googleRating}</p>
          <div className="flex items-center gap-3 mt-1">
            <span data-testid="google-rating-value" className="font-mono text-3xl text-gold leading-none">{rating}</span>
            <span className="flex items-center gap-0.5" aria-label={`${rating} / 5`}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={15} className={i < Math.round(GOOGLE_REVIEW.rating) ? "fill-gold text-gold" : "text-line"} />
              ))}
            </span>
            <span data-testid="google-rating-count" className="text-dim text-sm">
              · {GOOGLE_REVIEW.count} {t.testimonials.reviews}
            </span>
          </div>
        </div>
      </div>
      <a
        href={GOOGLE_REVIEW.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="google-review-button"
        className="group sm:ml-auto inline-flex items-center gap-2 border border-gold/60 text-gold px-5 py-3 text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors duration-300"
      >
        {t.testimonials.reviewBtn}
        <ExternalLink size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </div>
  );
};

export const Testimonials = () => {
  const { t, c } = useLang();
  return (
    <section id="testimonials" data-testid="testimonials-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
      <SectionHeading kicker={t.testimonials.kicker} title={<Accent parts={t.testimonials.title} />} testid="testimonials-heading" />

      <Reveal>
        <GoogleRating />
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6 md:gap-8">
        {c.TESTIMONIALS.map((tm, i) => (
          <Reveal key={i} delay={i * 0.1} className="h-full">
            <figure
              data-testid={`testimonial-card-${i}`}
              className="h-full border border-line bg-ink p-8 md:p-10 flex flex-col transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-gold/40"
            >
              <Quote size={24} className="text-gold mb-6" strokeWidth={1.5} />
              <blockquote className="font-serif italic text-xl md:text-2xl text-mist leading-snug flex-1 mb-8">„{tm.quote}“</blockquote>
              <figcaption>
                <p className="text-mist text-sm font-medium">{tm.name}</p>
                <p className="text-dim text-xs mt-1">{tm.role}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
